<template>
  <nav v-if="showNavbar" class="border-b bg-card sticky top-0 z-50">
    <div class="container mx-auto px-4">
      <div class="flex h-16 items-center justify-between">
        <!-- Logo/Brand -->
        <div class="flex items-center space-x-2 sm:space-x-4">
          <router-link to="/" class="flex items-center space-x-2">
            <div class="h-8 w-8 rounded bg-primary"></div>
            <span class="text-lg sm:text-xl font-bold">Schwab Options</span>
          </router-link>
        </div>

        <!-- Desktop Navigation Links -->
        <div class="hidden lg:flex items-center space-x-6">
          <router-link
            to="/"
            class="text-sm font-medium transition-colors hover:text-primary"
            :class="{ 'text-primary': $route.name === 'positions' }"
          >
            Positions
          </router-link>
          <router-link
            to="/symbols"
            class="text-sm font-medium transition-colors hover:text-primary"
            :class="{ 'text-primary': $route.name === 'symbols' }"
          >
            Symbols
          </router-link>
          <router-link
            to="/charts"
            class="text-sm font-medium transition-colors hover:text-primary"
            :class="{ 'text-primary': $route.name === 'charts' }"
          >
            Charts
          </router-link>
          <router-link
            to="/blacklist"
            class="text-sm font-medium transition-colors hover:text-primary"
            :class="{ 'text-primary': $route.name === 'blacklist' }"
          >
            Blacklist
          </router-link>
          <router-link
            to="/token"
            class="text-sm font-medium transition-colors hover:text-primary"
            :class="{ 'text-primary': $route.name === 'token' }"
          >
            Token
          </router-link>
        </div>

        <!-- Desktop Actions -->
        <div class="hidden lg:flex items-center space-x-3">
          <!-- Initial Cash Display/Edit -->
          <div class="flex items-center space-x-2">
            <span class="text-sm text-muted-foreground hidden xl:inline">Initial Cash:</span>
            <button
              @click="editCash"
              class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted h-9 px-3"
            >
              {{ formatCurrency(initialCash) }}
              <svg
                class="ml-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </div>
          <ThemeIndicator />
          <button
            @click="refreshData"
            :disabled="isRefreshing"
            class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3"
          >
            <svg
              v-if="isRefreshing"
              class="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <svg
              v-else
              class="mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
            <span class="hidden xl:inline">Refresh</span>
          </button>
          <!-- User info and logout -->
          <div class="flex items-center space-x-2">
            <span class="text-sm text-muted-foreground hidden xl:inline">Welcome, {{ username }}</span>
            <button
              @click="handleLogout"
              class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted h-9 px-3 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              title="Logout"
            >
              <svg
                class="h-4 w-4 xl:mr-2"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" x2="12" y1="12" y2="12" />
              </svg>
              <span class="hidden xl:inline">Logout</span>
            </button>
          </div>
        </div>

        <!-- Mobile Menu Button -->
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="lg:hidden inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted h-9 w-9"
          aria-label="Toggle menu"
        >
          <svg
            v-if="!mobileMenuOpen"
            class="h-6 w-6"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <svg
            v-else
            class="h-6 w-6"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Mobile Menu -->
      <div
        v-if="mobileMenuOpen"
        class="lg:hidden border-t py-4 space-y-4"
      >
        <!-- Mobile Navigation Links -->
        <div class="flex flex-col space-y-2">
          <router-link
            to="/"
            @click="mobileMenuOpen = false"
            class="text-sm font-medium transition-colors hover:text-primary px-2 py-2 rounded-md"
            :class="{ 'text-primary bg-muted': $route.name === 'positions' }"
          >
            Positions
          </router-link>
          <router-link
            to="/symbols"
            @click="mobileMenuOpen = false"
            class="text-sm font-medium transition-colors hover:text-primary px-2 py-2 rounded-md"
            :class="{ 'text-primary bg-muted': $route.name === 'symbols' }"
          >
            Symbols
          </router-link>
          <router-link
            to="/charts"
            @click="mobileMenuOpen = false"
            class="text-sm font-medium transition-colors hover:text-primary px-2 py-2 rounded-md"
            :class="{ 'text-primary bg-muted': $route.name === 'charts' }"
          >
            Charts
          </router-link>
          <router-link
            to="/blacklist"
            @click="mobileMenuOpen = false"
            class="text-sm font-medium transition-colors hover:text-primary px-2 py-2 rounded-md"
            :class="{ 'text-primary bg-muted': $route.name === 'blacklist' }"
          >
            Blacklist
          </router-link>
          <router-link
            to="/token"
            @click="mobileMenuOpen = false"
            class="text-sm font-medium transition-colors hover:text-primary px-2 py-2 rounded-md"
            :class="{ 'text-primary bg-muted': $route.name === 'token' }"
          >
            Token
          </router-link>
        </div>

        <!-- Mobile Actions -->
        <div class="flex flex-col space-y-3 pt-3 border-t">
          <div class="flex items-center justify-between px-2">
            <span class="text-sm text-muted-foreground">Welcome, {{ username }}</span>
          </div>
          <div class="flex items-center justify-between px-2">
            <span class="text-sm text-muted-foreground">Initial Cash:</span>
            <button
              @click="editCash"
              class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted h-9 px-3"
            >
              {{ formatCurrency(initialCash) }}
              <svg
                class="ml-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </div>
          <div class="flex items-center justify-between px-2">
            <span class="text-sm text-muted-foreground">Theme:</span>
            <ThemeIndicator />
          </div>
          <button
            @click="refreshData"
            :disabled="isRefreshing"
            class="w-full inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3"
          >
            <svg
              v-if="isRefreshing"
              class="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <svg
              v-else
              class="mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
            Refresh Data
          </button>
          <button
            @click="handleLogout"
            class="w-full inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted h-9 px-3 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            <svg
              class="mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" x2="12" y1="12" y2="12" />
            </svg>
            Logout
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { apiClient } from "@/lib/api";
import { useInitialCash } from "@/lib/initialCash";
import { formatCurrency } from "@/lib/utils";
import { getUsername, logout as authLogout, isAuthenticated } from "@/lib/auth";
import ThemeIndicator from "@/components/ThemeIndicator.vue";

const router = useRouter();
const route = useRoute();
const isRefreshing = ref(false);
const mobileMenuOpen = ref(false);
const username = ref(getUsername() || "");
const { initialCash, setInitialCash } = useInitialCash();

// Only show navbar when authenticated and not on login page
const showNavbar = computed(() => {
  return isAuthenticated() && route.name !== 'login';
});

// Close mobile menu when route changes
watch(() => route.path, () => {
  mobileMenuOpen.value = false;
});

const handleLogout = async () => {
  await apiClient.logout();
  authLogout();
  router.push("/login");
};

const refreshData = async () => {
  isRefreshing.value = true;
  try {
    // Clear cache to force fresh data
    await apiClient.clearCache();
    // Reload the page to refresh all data
    window.location.reload();
  } catch (error) {
    console.error("Failed to refresh data:", error);
  } finally {
    isRefreshing.value = false;
  }
};

const editCash = () => {
  const newCash = prompt("Enter initial cash amount:", initialCash.value.toString());
  if (newCash !== null) {
    const value = parseInt(newCash.replace(/[^0-9]/g, ""), 10);
    if (!isNaN(value) && value > 0) {
      setInitialCash(value);
      // Reload to refresh all data with new cash value
      window.location.reload();
    } else if (newCash !== "") {
      alert("Please enter a valid positive number");
    }
  }
};
</script>
